import React from "react";
import { useSelector } from "react-redux";
import { FileAudio, FileText, FileVideo } from "lucide-react";
import docIcon from "assets/imgs/doc_image.png";
import imageIcon from "assets/imgs/image_file.png";
import pngIcon from "assets/imgs/png_image.png";
import { selectUserFormData } from "app/redux/selector";
import type { TicketAttachmentFile } from "app/containers/Features/containers/Ticket/types";

const RENAME_INPUT_FOCUS_DELAY_MS = 400;

type UseUploadFilePresentationParams = {
  renamingFileId?: string | null;
};

type UseUploadFilePresentationResult = {
  renameInputRef: React.RefObject<HTMLInputElement>;
  renderFileMedia: (file: TicketAttachmentFile) => React.ReactNode;
  avatar: any;
  userName: string;
};

export const useUploadFilePresentation = ({
  renamingFileId,
}: UseUploadFilePresentationParams): UseUploadFilePresentationResult => {
  const userDetails = useSelector(selectUserFormData);
  const renameInputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (!renamingFileId) return;
    const focusTimeout = window.setTimeout(() => {
      const el = renameInputRef.current;
      if (!el) return;
      el.focus({ preventScroll: true });
      el.select();
    }, RENAME_INPUT_FOCUS_DELAY_MS);
    return () => window.clearTimeout(focusTimeout);
  }, [renamingFileId]);

  const renderFileMedia = React.useCallback((file: TicketAttachmentFile) => {
    switch (file.type) {
      case "doc":
        return <img src={docIcon} alt="doc_image" sizes="100%" />;
      case "pdf":
        return <img src={pngIcon} alt="png_image" sizes="100%" />;
      case "image":
        if (file.url) {
          return (
            <img
              src={file.url}
              alt={file.name}
              className="h-full w-full object-cover"
            />
          );
        }
        return (
          <img
            src={imageIcon}
            alt="image_file"
            style={{
              width: "-webkit-fill-available",
              height: "-webkit-fill-available",
            }}
          />
        );
      case "video":
        return (
          <div className="flex h-full w-full items-center justify-center bg-[#253858]">
            <FileVideo className="size-14 text-white/90" aria-hidden />
          </div>
        );
      case "audio":
        return (
          <div className="flex h-full w-full items-center justify-center bg-[#253858]">
            <FileAudio className="size-14 text-white/90" aria-hidden />
          </div>
        );
      case "text":
        return (
          <div className="flex h-full w-full items-center justify-center bg-[#253858]">
            <FileText className="size-14 text-white/90" aria-hidden />
          </div>
        );
    }
  }, []);

  return {
    renameInputRef,
    renderFileMedia,
    avatar: userDetails?.avatar,
    userName: userDetails?.fullName || "User",
  };
};

export default useUploadFilePresentation;
